import moment from "moment";
import VueMonthlyPicker from 'vue-monthly-picker'
import Select2Multiple from 'v-select2-multiple-component';
import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import CONFIG from "@/assets/js/config.js";
import axios from "axios";
import {
  required,
  maxLength,
} from "vuelidate/lib/validators";

const isNumber = (value) => {
  if (value === null || value === undefined || value === '') return true; 
  return !isNaN(Number(String(value).replace(/,/g, '')));
};

const toNumber = (value) =>
  Number(String(value ?? 0).replace(/,/g, '')) || 0;

export default {
  name: "paymentDetailCreate",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
    VueMonthlyPicker,
  },

  data() {
    return {
      CONFIG,
      netTotalSum: 0,
      grossTotalSum: 0,
      discountTotal: 0,
      totalAfterDiscount: 0,
      isLoading: false,
      submitted: false,
      empData: {
        employeeType: "1",
        employeeId: null,
        employeeableId: null,
        empNumber: '',
        positionId: null,
        position: '',
        selectedMonth: moment().format('YYYY-MM'),
        totalUnpaidLeaveDay: 0,
        totalAbsentLeaveDay: 0,
        totalLateHour: 0,
        travelAllowanceWorkingDays: 0,
        totalOvertimeHours: 0,
        oldLeftDay: 0,
        skillInputValues: {},
        additionInputValues: {},
        deductionInputValues: {},
        remarkInputValues: {},
      },
      employeeList: [],
      employeeOptions: [],
      skillAttributes: [],
      additionAttributes: [],
      deductionAttributes: [],
      remarkAttributes: [],
      totals: {
        totalSkill: 0,
        totalAddition: 0,
        totalDeduction: 0,
      },
      totalSalary: 0,
      isInitial: true,
      employeeNameList: [],
      fetchData: [],
      monthDisable: true,
      disabled: false,
      ssbFlag: false,
      lastDeductionChangedId: null,
      incomeTaxError: null,
      pitTimer: null,
      pitLoading: false,
    };
  },

  validations() {
    return {
      empData: {
        employeeType: { required },
        employeeId: { required },
        selectedMonth: { required },

        remarkInputValues: {
          $each: {
            maxLength: maxLength(200)
          }
        },

        skillInputValues: {
          $each: { 
            isNumber, 
            minValue: (v) => toNumber(v) >= 0, 
            maxValue: (v) => toNumber(v) <= 999999999 
          },
        },

        additionInputValues: {
          $each: { 
            isNumber, 
            minValue: (v) => toNumber(v) >= 0, 
            maxValue: (v) => toNumber(v) <= 999999999 
          }
        },

        deductionInputValues: {
          $each: { 
            isNumber, 
            minValue: (v) => toNumber(v) >= 0, 
            maxValue: (v) => toNumber(v) <= 999999999 
          }
        },

      }
    };
  },

  methods: {

    normalizeSectionHourlySetting(value) {
      if (value == CONFIG.SECTION_HOURLY_SETTING.HOURLY) {
        return CONFIG.SECTION_HOURLY_SETTING.HOURLY;
      }
      if (String(value).toLowerCase() === 'hourly') {
        return CONFIG.SECTION_HOURLY_SETTING.HOURLY;
      }
      return CONFIG.SECTION_HOURLY_SETTING.SECTION;
    },

    isPartTimeEmployment() {
      const employmentType = Number(this.empData.employmentType);
      return employmentType === CONFIG.EMPLOYMENT_TYPE.DAILY_PART_TIME
        || employmentType === CONFIG.EMPLOYMENT_TYPE.HOURLY_PART_TIME;
    },

    async fetchInitialData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.getLastMonthAndYear(),
          this.getEmployees(),
          this.getAllAttributesGrouped(),
        ]);
        this.isInitial = false;
      } catch (error) {
        console.error(error);
        this.$notification?.error?.(error.response?.data?.message || 'Failed to load initial data.', {
          timer: 5,
          position: 'bottomCenter',
        });
      }
    },

    async getLastMonthAndYear() {
      var returnData = await axios.get('/paymentAttributeDetail/get-last-month-year');
      if (returnData.data && returnData.data.last_month_year) {
        this.empData.selectedMonth = returnData.data.last_month_year;
      }
    },

    async getEmployees(type = null) {
      const fetchType = type || this.empData.employeeType;
      try {
        const res = await axios.get('/employee/list', {
          params: {
            employeeable_type: fetchType,
            exclude_payment_assigned: 1,
            require_position: 1,
            exclude_admin_hr: 1,
          },
        });
        const employees = res.data.data || [];
        this.employeeList = employees.map(emp => ({
          id: emp.id,
          employeeable_id: emp.employeeable_id,
          name: emp.name,
          emp_no: emp.employee_code,
          position_amount: 0,
        }));
        const defaultOption = [{ id: null, text: 'Select Employee Name' }];
        this.employeeOptions = defaultOption.concat(
          employees.map(emp => ({
            id: emp.id,
            text: `${emp.employee_code} - ${emp.name} (${emp.employeeable_type_text})`,
          }))
        );
      } catch (error) {
        console.error('Error fetching employees', error);
        this.$notification?.error?.(error.response?.data?.message || 'Failed to load employees.', {
          timer: 5,
          position: 'bottomCenter',
        });
        throw error;
      }
    },

    async getAllAttributesGrouped() {
      try {
        const res = await axios.get('/paymentAttribute/all-attributes-grouped');
        this.skillAttributes = res.data.S || [];
        this.additionAttributes = res.data.O || [];
        this.deductionAttributes = res.data.D || [];
        this.remarkAttributes = res.data.R || [];
        await this.declareInputValues();
      } catch (error) {
        console.error('Error fetching attributes', error);
        this.$notification?.error?.(error.response?.data?.message || 'Failed to load payment attributes.', {
          timer: 5,
          position: 'bottomCenter',
        });
        throw error;
      }
    },

    async calculatePIT() {
      var param = {
        'employeeType': this.empData.employeeType,
        'employeeId': this.empData.employeeId,
        'selectedMonth': this.empData.selectedMonth,
        'totalSalary': this.totalSalary,
        'annualBonus': this.getValueByName('addition', 'Annual Bonus'),
        'ssb': this.getValueByType('deduction', 'FS'),
        'skillTotal': this.totals.totalSkill,
      }
      try {
        const res = await axios.post('/paymentAttributeDetail/getMonthlyTax', param);
        const response = res.data;
        if (response.error) {
          this.incomeTaxError = response.error;
        } else {
          this.incomeTaxError = null;
          this.setValueByType('deduction', 'FI', response.monthlyTax);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store?.commit?.('logout');
          window.location.replace('login');
          return;
        }
        const message = this.formatValidationMessage(
          error.response?.data?.message,
          error.response?.data?.errors
        ) || 'Failed to calculate monthly tax.';
        this.incomeTaxError = message;
        console.error('Error on fetching monthly tax', error);
      }
    },

    async declareInputValues() {
      this.skillAttributes.forEach(attr => {
        this.$set(this.empData.skillInputValues, attr.id, "0.00");
      });
      this.additionAttributes.forEach(attr => {
        this.$set(this.empData.additionInputValues, attr.id, "0.00");
      });
      this.deductionAttributes.forEach(attr => {
        this.$set(this.empData.deductionInputValues, attr.id, "0.00");
      });
      this.remarkAttributes.forEach(attr => {
        this.$set(this.empData.remarkInputValues, attr.id, "");
      });

      this.totals = {
        totalSkill: 0,
        totalAddition: 0,
        totalDeduction: 0,
      }
    },

    async fetchCalculatedValuesByEmpId() {
      this.isLoading = true;
      const selected = this.employeeList.find(e => String(e.id) === String(this.empData.employeeId)) || null;
      this.empData.employeeableId = selected ? selected.employeeable_id : null;
      const param = {
        employee_id: this.empData.employeeId,
        employeeable_id: this.empData.employeeableId,
        employee_type: this.empData.employeeType,
        calculate_month: this.empData.selectedMonth,
      };
      try {
        const response = await axios.post('/paymentAttributeDetail/getAttributeValueByEmployee', param);
        const data = response.data;
        if (!data) {
          this.$notification?.error?.('Something went wrong while calculating.', {
            timer: 3,
            position: 'bottomCenter',
          });
          return;
        }
        this.empData.empNumber = data.employee_number || '';
        this.empData.position = data.position_name || '';
        this.fetchData = data;
        this.resetFetchData();
      } catch (error) {
        console.error('Error fetching calculated values:', error);

        const validationErrors = error.response?.data?.errors;
        let errorMessage = '';

        if (validationErrors) {
          const firstFieldKey = Object.keys(validationErrors)[0];
          errorMessage = validationErrors[firstFieldKey][0];
        } else {
          errorMessage = error.response?.data?.message || 'Failed to calculate payment values.';
        }

        this.$notification?.error?.(errorMessage, {
          timer: 5,
          position: 'bottomCenter',
        });
      } finally {
        this.isLoading = false;
      }
    },

    resetFetchData() {
      this.declareInputValues();
      this.setValueByType('addition', 'FO', this.fetchData.overtime_fee);
      this.setValueByType('addition', 'FB', this.fetchData.no_late_no_leave);
      this.setValueByType('addition', 'FA', this.fetchData.annual_refund);
      this.setValueByType('addition', 'FT', this.fetchData.travelling_allowance);
      this.setValueByType('skill', 'FVB', this.fetchData.basic_salary);
      this.setValueByType('deduction', 'FP', this.fetchData.late_fine_fee);
      this.setValueByType('deduction', 'FL', this.fetchData.leave_fine_fee);

      if (this.fetchData.skill_input_values) {
        Object.entries(this.fetchData.skill_input_values).forEach(([attrId, value]) => {
          this.$set(this.empData.skillInputValues, attrId, value);
        });
      }

      this.ssbFlag = this.fetchData.ssb_flag;
      this.empData.totalUnpaidLeaveDay = this.fetchData.total_unpaid_leave_day;
      this.empData.totalAbsentLeaveDay = this.fetchData.total_absent_leave_day;
      this.empData.totalLateHour = this.fetchData.total_late_hour;
      this.empData.travelAllowanceWorkingDays = this.fetchData.travel_allowance_working_days;
      this.empData.totalOvertimeHours = this.fetchData.total_overtime_hours;
      this.empData.oldLeftDay = this.fetchData.old_left_day;
      this.empData.partTimeWorkUnits = this.fetchData.part_time_work_units || 0;
      this.empData.partTimeRate = this.fetchData.part_time_rate || 0;
      this.empData.partTimeRateType = this.fetchData.part_time_rate_type || '';
      this.empData.partTimeSectionHourly = this.normalizeSectionHourlySetting(this.fetchData.part_time_section_hourly);
      this.empData.employmentType = this.fetchData.employment_type || CONFIG.EMPLOYMENT_TYPE.FULL_TIME;
    },

    formatNumber(val) {
      return this.$options.filters.numDeciFormat(val);
    },

    async parseNumber(val) {
      const num = parseFloat(String(val).replace(/,/g, ''));
      return isNaN(num) ? 0 : Math.abs(num);
    },

    async onNumberInput(e, id, group) {
      const raw = await this.parseNumber(e.target.value);
      this.empData[`${group}InputValues`][id] = raw;
      if (group === 'deduction') {
        this.lastDeductionChangedId = id;
      }
    },

    setValueByType(titleType, attriType, value) {
      const attrs = this[`${titleType}Attributes`];
      const inputs = this.empData[`${titleType}InputValues`];
      attrs.filter(a => a && a.type == attriType)
            .forEach(a => {
              if (this.$set) this.$set(inputs, a.id, value);
            })
    },

    getValueByType(titleType, attriType) {
      const attrs = this[`${titleType}Attributes`];
      const inputs = this.empData[`${titleType}InputValues`];
      return attrs.filter(a => a && a.type == attriType)
                .map(a => {
                  const value = this.$get ? this.$get(inputs, a.id) : inputs[a.id];
                  return value ? Number(String(value).replace(/,/g, '')) : 0;
                })[0] || 0;
    },

    getValueByName(titleType, attrName) {
      const attrs = this[`${titleType}Attributes`];
      const inputs = this.empData[`${titleType}InputValues`];

      return attrs
          .filter(a => a && a.attribute_name === attrName)
          .map(a => {
            const value = this.$get ? this.$get(inputs, a.id) : inputs[a.id];
            return value ? Number(String(value).replace(/,/g, '')) : 0;
          })[0] || 0;
    },

    getAttrIdByType(titleType, attriType) {
      const attrs = this[`${titleType}Attributes`] || [];
      const a = attrs.find(x => x && x.type === attriType);
      return a ? a.id : null;
    },

    /**
     * Sum of deduction values excluding types FP, FL (used for totalSalary).
     */
    getSumOfDeductionForTotal() {
      let sumOfDeduction = 0;
      (this.deductionAttributes || []).forEach((attr) => {
        const type = attr && attr.type;
        if (type === 'FP' || type === 'FL') return;
        const value = this.empData.deductionInputValues[attr.id];
        sumOfDeduction += toNumber(value);
      });
      return sumOfDeduction;
    },

    formatValidationMessage(message, errors) {
      if (message) return String(message).trim();
      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0];
        if (firstKey) {
          const msgs = Array.isArray(errors[firstKey]) ? errors[firstKey] : [errors[firstKey]];
          const first = msgs.find((m) => m);
          if (first) return String(first).trim();
        }
      }
      return '';
    },

    async submitPayment() {
      if (this.disabled) return;
      this.disabled = true;
      this.submitted = true;

      if (this.submitted && this.$v.empData.$invalid) {
        this.disabled = false;
        return;
      }

      this.isLoading = true;

      const payload = { ...this.empData };
      delete payload.positionId;
      delete payload.position;
      delete payload.empNumber;

      try {
        const res = await axios.post('paymentAttributeDetail/registerAttributeValueByEmployee', payload);
        if (res.data && res.data.success === true) {
          this.$notification?.success?.('Payment Attribute Detail registered successfully.', {
            timer: 3,
            position: 'bottomCenter',
          });
          this.$router.push({ name: 'paymentAttributeDetailList' });
        } else {
          const msg = this.formatValidationMessage(res.data?.message, res.data?.errors) || 'Registration failed.';
          this.$notification?.error?.(msg, { timer: 5, position: 'bottomCenter' });
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store?.commit?.('logout');
          window.location.replace('login');
          return;
        }
        const msg = this.formatValidationMessage(
          error.response?.data?.message,
          error.response?.data?.errors
        ) || 'Failed to save payment attribute detail.';
        this.$notification?.error?.(msg, { timer: 5, position: 'bottomCenter' });
      } finally {
        this.disabled = false;
        this.isLoading = false;
      }
    },

    async setSocialSecurity() {
      if (this.isPartTimeEmployment()) return;
      if (!this.ssbFlag) {
        var ssb = this.getValueByType('deduction', 'FS');
        this.setValueByType('deduction', 'FS', ssb);
        return;
      }
      const skillBalance = this.totals.totalSkill;
      var socialSecurity = (skillBalance >= 300000) ? 6000 
                          : skillBalance >= 150000 ? skillBalance * 0.02
                          : 0;
      this.setValueByType('deduction', 'FS', socialSecurity);
    },

    back() {
      this.$router.go(-1);
    },
  },

  computed: {
  },

  watch: {
    'empData.employeeType': async function (newType) {
      this.isLoading = true;
      this.getEmployees(newType).finally(() => {
        this.isLoading = false;
      });

      this.empData.employeeId = null;
      this.empData.employeeableId = null;
      this.empData.empNumber = '';
      this.empData.positionId = null;
      this.empData.position = '';
      await this.declareInputValues();
    },
    'empData.employeeId': async function (newId) {
      if (newId !== null && newId !== 0 && newId !== '0') {
        this.monthDisable = false;
        await this.declareInputValues();
        await this.fetchCalculatedValuesByEmpId();
      } else {
        this.monthDisable = true;
        this.empData.employeeableId = null;
        this.empData.empNumber = '';
        this.empData.positionId = null;
        this.empData.position = '';
        await this.declareInputValues();
      }
    },
    'empData.selectedMonth': async function () { 
      if (!this.isInitial) {
        this.empData.selectedMonth = moment(this.empData.selectedMonth).format('YYYY-MM');
        await this.declareInputValues();
        await this.fetchCalculatedValuesByEmpId();
      }
    },
    'empData.skillInputValues': {
      deep: true,
      handler(newObj) {
        this.totals.totalSkill = Object.values(newObj)
          .reduce((sum, v) => sum + toNumber(v), 0);
        this.setSocialSecurity();
      }
    },
    'empData.additionInputValues': {
      deep: true,
      handler(newObj) {
        this.totals.totalAddition = Object.values(newObj)
          .reduce((sum, v) => sum + toNumber(v), 0);
      }
    },
    'empData.deductionInputValues': {
      deep: true,
      handler(newObj) {
        this.totals.totalDeduction = Object.values(newObj)
          .reduce((sum, v) => sum + toNumber(v), 0);
      }
    },
    totals: {
      deep: true,
      handler() {
        const netSalary = this.totals.totalSkill + this.totals.totalAddition - this.totals.totalDeduction;
        const sumOfDeduction = this.getSumOfDeductionForTotal();
        this.totalSalary = netSalary + sumOfDeduction;
      }
    },
    totalSalary: function () {
      if (this.isPartTimeEmployment()) return;
      if (!this.fetchData || Object.keys(this.fetchData).length === 0) return;
      if (this.totalSalary <= 0) return;
      const fiId = this.getAttrIdByType('deduction', 'FI');
      if (fiId != null && this.lastDeductionChangedId === fiId) {
        this.lastDeductionChangedId = null;
        return;
      }
      this.lastDeductionChangedId = null;

      clearTimeout(this.pitTimer);
      this.pitTimer = setTimeout(async () => {
        if (this.pitLoading) return;
        if (this.totalSalary <= 0) return;
        this.$v.empData.$touch();
        if (this.$v.empData.$invalid) return;
        this.pitLoading = true;
        try {
          await this.calculatePIT();
        } finally {
          this.pitLoading = false;
        }
      }, 300);
    },
  },

  async mounted() {
    this.isLoading = true;
    try {
      await this.fetchInitialData();
    } finally {
      this.isLoading = false; 
    }
  },
};
